import { createContext, useContext, useCallback } from 'react'
import { useAuth } from './AuthContext'

const PermissionContext = createContext(null)

export const usePermissions = () => {
  const context = useContext(PermissionContext)
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider')
  }
  return context
}

export const PermissionProvider = ({ children }) => {
  const {
    permissions,
    loading,
    isAdmin,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    fetchUserPermissions,
  } = useAuth()

  // Check permission for a resource action (e.g., 'assets', 'view')
  const can = useCallback((resource, action) => {
    const permission = `${resource}.${action}`
    return hasPermission(permission)
  }, [hasPermission])

  // Check if user can view a resource
  const canView = useCallback((resource) => can(resource, 'view'), [can])

  // Check if user can create a resource
  const canCreate = useCallback((resource) => can(resource, 'create'), [can])

  // Check if user can edit a resource
  const canEdit = useCallback((resource) => can(resource, 'edit'), [can])

  // Check if user can delete a resource
  const canDelete = useCallback((resource) => can(resource, 'delete'), [can])

  const value = {
    permissions,
    isAdmin,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    can,
    canView,
    canCreate,
    canEdit,
    canDelete,
    refreshPermissions: fetchUserPermissions,
  }

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  )
}

export default PermissionContext
